import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import * as Sentry from "@sentry/react"
import { purgeAppStorage } from "Shared/components/ResilientErrorBoundary"
import { WebSocketStatusContext } from "Shared/context/WebSocketStatus.context"
import { config } from "Utils/config"
import { createPortal } from "react-dom"

import * as am5 from "@amcharts/amcharts5"
import * as am5xy from "@amcharts/amcharts5/xy"
import am5themes_Dark from "@amcharts/amcharts5/themes/Dark"

type Tab = "debug" | "health" | "perf" | "sync" | "recovery"

type PingHTTP = {
  kind: "http"
  url: string
  ok: boolean
  status?: number
  totalMs: number
  ttfbMs?: number
  dlMs?: number
  transferSize?: number
  bodySize?: number
  protocol?: string
  fromCache?: boolean
  note?: string
  at: number
}
type PingWS = {
  kind: "ws"
  url: string
  ok: boolean
  connectMs?: number
  closeCode?: number
  closeReason?: string
  note?: string
  at: number
}
type PingAny = PingHTTP | PingWS

type Props = {
  accessToken?: string
  businessId?: string | number
  user?: { id?: any; email?: string; firstname?: string; lastname?: string } | null
  isSyncing: boolean
  onToggleSync: (next: boolean) => void
  onSyncOnce?: () => void
}

const OPEN_HOTKEY = { ctrl: true, shift: true, key: "u" } as const
const P95_WARN = 700
const P95_CRIT = 1500

const hotkeyMatch = (e: KeyboardEvent) =>
  !!OPEN_HOTKEY.ctrl === e.ctrlKey &&
  !!OPEN_HOTKEY.shift === e.shiftKey &&
  !!OPEN_HOTKEY.alt === e.altKey &&
  e.key?.toLowerCase() === OPEN_HOTKEY.key

const mask = (v?: string, show = false) => {
  if (!v) return ""
  if (show) return v
  const n = Math.min(6, Math.floor(v.length / 4))
  return v.slice(0, n) + "…" + v.slice(-n)
}
const mean = (a: number[]) =>
  a.length ? Math.round(a.reduce((x, y) => x + y, 0) / a.length) : undefined
const p95 = (a: number[]) => {
  if (!a.length) return undefined
  const s = [...a].sort((x, y) => x - y)
  return s[Math.floor(0.95 * (s.length - 1))]
}

// ----------------- PING -----------------
async function pingHTTP(url: string): Promise<PingHTTP> {
  const t0 = performance.now()
  const u = new URL(url)
  u.searchParams.set("_rt", String(Date.now()))
  if (location.protocol === "https:" && u.protocol === "http:")
    return {
      kind: "http",
      url,
      ok: false,
      totalMs: 0,
      note: "blocked:mixed-content",
      at: Date.now(),
    }

  let ok = false,
    status: number | undefined,
    note: string | undefined
  try {
    const res = await fetch(u.toString(), { method: "GET", mode: "cors", cache: "no-store" })
    ok = res.ok
    status = res.status
  } catch {
    ok = false
  }
  const totalMs = Math.round(performance.now() - t0)
  const entries = performance.getEntriesByName(u.toString()) as PerformanceResourceTiming[]
  const last = entries.at(-1)
  return {
    kind: "http",
    url,
    ok,
    status,
    totalMs,
    ttfbMs: last ? Math.round(last.responseStart - last.startTime) : undefined,
    dlMs: last ? Math.round(last.responseEnd - last.responseStart) : undefined,
    transferSize: (last as any)?.transferSize,
    bodySize: (last as any)?.encodedBodySize,
    protocol: (last as any)?.nextHopProtocol,
    fromCache: (last as any)?.transferSize === 0 && ((last as any)?.encodedBodySize ?? 0) > 0,
    at: Date.now(),
  }
}

function pingWS(wsUrl: string): Promise<PingWS> {
  return new Promise(resolve => {
    let note: string | undefined
    if (location.protocol === "https:" && wsUrl.startsWith("ws:")) {
      return resolve({
        kind: "ws",
        url: wsUrl,
        ok: false,
        note: "blocked:mixed-content",
        at: Date.now(),
      })
    }
    let url = wsUrl
    if (!/\/graphql$/.test(url)) url = url.replace(/\/?$/, "/graphql")
    const t0 = performance.now()
    const ws = new WebSocket(url, "graphql-transport-ws")
    let connectMs: number | undefined,
      done = false
    const finish = (ok: boolean, extra: Partial<PingWS> = {}) => {
      if (done) return
      done = true
      try {
        ws.close()
      } catch {}
      resolve({ kind: "ws", url, ok, connectMs, at: Date.now(), note, ...extra })
    }
    const to = setTimeout(() => finish(false, { note: "timeout" }), 3000)
    ws.addEventListener("open", () => {
      connectMs = Math.round(performance.now() - t0)
      try {
        ws.close(1000, "probe")
      } catch {}
    })
    ws.addEventListener("close", e => {
      clearTimeout(to)
      finish(typeof connectMs === "number", { closeCode: e.code, closeReason: e.reason })
    })
    ws.addEventListener("error", () => {
      clearTimeout(to)
      finish(false)
    })
  })
}

// ----------------- HEALTH -----------------
type HealthKind = "readiness" | "liveness" | "meta"
type ReadinessSample = {
  at: number
  url: string
  ok: boolean
  totalMs: number
  status?: string
  indicators?: Record<string, string>
  note?: string
  kind: HealthKind
  label: string
}

async function fetchHealth(
  baseUrl: string,
  kind: HealthKind,
  label: string
): Promise<ReadinessSample> {
  const t0 = performance.now()
  const url = baseUrl.replace(/\/$/, "") + (kind === "meta" ? "/meta.json" : `/health/${kind}`)
  const ctrl = new AbortController()
  const to = setTimeout(() => ctrl.abort("timeout"), 2500)
  let ok = false,
    status: string | undefined,
    indicators: Record<string, string> | undefined,
    note: string | undefined

  try {
    const res = await fetch(url + (kind === "meta" ? `?ts=${Date.now()}` : ""), {
      method: "GET",
      mode: "cors",
      cache: "no-store",
      signal: ctrl.signal,
    })
    if (!res.ok) note = `http:${res.status}`

    if (kind === "meta") {
      const j = await res.json().catch(() => (note = "json"))
      status = res.ok ? "ok" : "error"
      indicators = {}
      if (j && typeof j === "object") {
        if (j.release) indicators.release = String(j.release)
        if (j.buildDate || j.build || j.version)
          indicators.build = String(j.buildDate || j.build || j.version)
      }
      ok = res.ok
    } else {
      const j = await res.json().catch(() => (note = "json"))
      if (j) {
        status = j.status
        const det = j.details || j.info || {}
        indicators = {}
        for (const k of Object.keys(det)) {
          let s = det[k]?.status ?? det[k]
          if (typeof s !== "string") s = String(s)
          indicators[k] = s
        }
        ok = j.status === "ok" || j.status === "up"
      }
    }
  } catch (e: any) {
    note = e?.name === "AbortError" || e === "timeout" ? "timeout" : "error"
  } finally {
    clearTimeout(to)
  }

  const totalMs = Math.round(performance.now() - t0)
  return { at: Date.now(), url, ok, totalMs, status, indicators, note, kind, label }
}

// ----------------- GraphQL probes -----------------
async function probeGraphQLRoundtrip(endpoint: string, token?: string) {
  const t0 = performance.now()
  try {
    const r = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ query: "query { __typename }" }),
      cache: "no-store",
    })
    const ms = Math.round(performance.now() - t0)
    return { ok: r.ok, ms, status: r.status }
  } catch {
    return { ok: false, ms: Math.round(performance.now() - t0) }
  }
}
function probeGraphQLWsAck(
  wsBase: string,
  token?: string
): Promise<{ ok: boolean; ms?: number; note?: string }> {
  return new Promise(resolve => {
    if (!wsBase) return resolve({ ok: false, note: "no-ws" })
    let url = wsBase.replace(/\/?$/, "/graphql")
    const ws = new WebSocket(url, "graphql-transport-ws")
    const t0 = performance.now()
    let done = false
    const finish = (ok: boolean, note?: string) => {
      if (done) return
      done = true
      try {
        ws.close()
      } catch {}
      resolve({ ok, ms: Math.round(performance.now() - t0), note })
    }
    const to = setTimeout(() => finish(false, "timeout"), 4000)
    ws.onopen = () =>
      ws.send(
        JSON.stringify({
          type: "connection_init",
          payload: token ? { headers: { Authorization: `Bearer ${token}` } } : {},
        })
      )
    ws.onmessage = ev => {
      try {
        const m = JSON.parse(ev.data)
        if (m.type === "connection_ack") {
          clearTimeout(to)
          finish(true)
        }
      } catch {}
    }
    ws.onerror = () => {
      clearTimeout(to)
      finish(false, "error")
    }
  })
}

// ----------------- Charts -----------------
function LatencyChart({ data }: { data: Array<{ at: number; totalMs: number }> }) {
  const divRef = useRef<HTMLDivElement | null>(null)
  const rootRef = useRef<am5.Root | null>(null)

  useEffect(() => {
    if (!divRef.current) return
    const root = am5.Root.new(divRef.current)
    rootRef.current = root
    root.setThemes([am5themes_Dark.new(root)])
    const chart = root.container.children.push(
      am5xy.XYChart.new(root, { panX: false, panY: false, wheelX: "none", wheelY: "none" })
    )
    const xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        baseInterval: { timeUnit: "second", count: 1 },
        renderer: am5xy.AxisRendererX.new(root, { inside: true, minGridDistance: 20 }),
      })
    )
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, { renderer: am5xy.AxisRendererY.new(root, { inside: true }) })
    )
    const series = chart.series.push(
      am5xy.LineSeries.new(root, { xAxis, yAxis, valueYField: "value", valueXField: "date" })
    )
    series.strokes.template.setAll({ strokeWidth: 2, opacity: 0.9 })
    series.data.setAll(data.map(d => ({ date: d.at, value: d.totalMs })))
    xAxis.get("renderer").labels.template.setAll({ forceHidden: true })
    yAxis.get("renderer").labels.template.setAll({ forceHidden: true })
    chart.setAll({ paddingLeft: 0, paddingRight: 0, paddingTop: 0, paddingBottom: 0 })
    return () => {
      root.dispose()
      rootRef.current = null
    }
  }, [])
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const chart = root.container.children.getIndex(0) as am5xy.XYChart
    const series = chart?.series.getIndex(0) as am5xy.LineSeries
    if (series) series.data.setAll(data.map(d => ({ date: d.at, value: d.totalMs })))
  }, [data])
  return <div ref={divRef} style={{ width: "100%", height: 120 }} />
}

// ----------------- PERF hooks -----------------
function useFPS(enabled = true) {
  const [fps, setFps] = useState<number>(0)
  const history = useRef<{ at: number; v: number }[]>([])
  useEffect(() => {
    if (!enabled) return
    let prev = performance.now(),
      frames = 0,
      id = 0
    const tick = () => {
      const now = performance.now()
      frames++
      const dt = now - prev
      if (dt >= 1000) {
        const v = Math.round((frames * 1000) / dt)
        setFps(v)
        history.current = [...history.current, { at: Date.now(), v }].slice(-120)
        frames = 0
        prev = now
      }
      id = requestAnimationFrame(tick)
    }
    id = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(id)
  }, [enabled])
  return { fps, history: history.current }
}
function useLoopLag(enabled = true, intervalMs = 500) {
  const [lag, setLag] = useState<number>(0)
  const hist = useRef<{ at: number; ms: number }[]>([])
  useEffect(() => {
    if (!enabled) return
    let next = performance.now() + intervalMs
    const id = setInterval(() => {
      const now = performance.now()
      const d = Math.max(0, now - next)
      setLag(Math.round(d))
      hist.current = [...hist.current, { at: Date.now(), ms: Math.round(d) }].slice(-120)
      next = now + intervalMs
    }, intervalMs)
    return () => clearInterval(id)
  }, [enabled, intervalMs])
  const arr = hist.current.map(x => x.ms).sort((a, b) => a - b)
  const p95v = arr.length ? arr[Math.floor(0.95 * (arr.length - 1))] : 0
  return { lag, p95: p95v, history: hist.current }
}
function useJsHeap(enabled = true) {
  const [heap, setHeap] = useState<{ used?: number; total?: number }>({})
  useEffect(() => {
    if (!enabled) return
    const id = setInterval(() => {
      const m: any = (performance as any).memory
      if (m)
        setHeap({
          used: Math.round(m.usedJSHeapSize / 1048576),
          total: Math.round(m.totalJSHeapSize / 1048576),
        })
    }, 2000)
    return () => clearInterval(id)
  }, [enabled])
  return heap
}
function useLongTasks(enabled = true) {
  const [count, setCount] = useState(0),
    [maxMs, setMaxMs] = useState(0),
    [totalMs, setTotalMs] = useState(0)
  useEffect(() => {
    if (!enabled || typeof PerformanceObserver === "undefined") return
    const obs = new PerformanceObserver(list => {
      // @ts-ignore
      for (const e of list.getEntries()) {
        const d = Math.round(e.duration)
        setCount(c => c + 1)
        setTotalMs(t => t + d)
        setMaxMs(m => Math.max(m, d))
      }
    })
    try {
      /* @ts-ignore */ obs.observe({ type: "longtask", buffered: true })
    } catch {}
    return () => obs.disconnect()
  }, [enabled])
  return { count, maxMs, totalMs }
}
function useEnvInfo() {
  const [storage, setStorage] = useState<{ usage?: number; quota?: number }>({})
  const [battery, setBattery] = useState<{ level?: number; charging?: boolean }>({})
  useEffect(() => {
    const nav: any = navigator
    if (nav.storage?.estimate)
      nav.storage.estimate().then((e: any) =>
        setStorage({
          usage: Math.round((e.usage || 0) / 1048576),
          quota: Math.round((e.quota || 0) / 1048576),
        })
      )
    if (nav.getBattery)
      nav
        .getBattery()
        .then((b: any) => {
          const upd = () => setBattery({ level: Math.round(b.level * 100), charging: b.charging })
          b.addEventListener("levelchange", upd)
          b.addEventListener("chargingchange", upd)
          upd()
        })
        .catch(() => {})
  }, [])
  const conn: any = (navigator as any).connection || {}
  return {
    cores: navigator.hardwareConcurrency,
    deviceMemory: (navigator as any).deviceMemory,
    ua: navigator.userAgent,
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
    langs: navigator.languages?.join(", "),
    net: {
      type: conn.effectiveType,
      downlink: conn.downlink,
      rtt: conn.rtt,
      saveData: conn.saveData,
    },
    storage,
    battery,
  }
}

// ----------------- Component -----------------
export default function UnifiedConsole(props: Props) {
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState<Tab>("debug")
  const [showSecrets, setShowSecrets] = useState(false)
  const [busy, setBusy] = useState(false)
  const [crash, setCrash] = useState(false)
  const [metaRel, setMetaRel] = useState<string | undefined>()
  const wsStatus = useContext(WebSocketStatusContext)

  const [samples, setSamples] = useState<Record<string, PingAny[]>>({})
  const [healthData, setHealthData] = useState<Record<string, ReadinessSample[]>>({})
  const [burst, setBurst] = useState(false)
  const [healthBurst, setHealthBurst] = useState(false)

  const envLabel = (
    config.ENVIRONMENT ??
    config.ENVIRONMENT ??
    config.NODE_ENV ??
    "local"
  ).toUpperCase()

  // ⚠️ ping HTTP = /health/readiness (pas /graphql)
  const apolloHealth = `${config.APOLLO_SERVER_URL}/health/readiness`
  const apolloWs = `${config.APOLLO_WS_URL}`

  // “spécial en 5” : uniquement en local
  const specialBase = useMemo(() => {
    if (location.hostname === "localhost" || location.hostname === "127.0.0.1")
      return "http://localhost:5253"
    return undefined
  }, [])

  const [openerTarget, setOpenerTarget] = useState<HTMLElement | null>(null)
  useEffect(() => {
    const update = () => setOpenerTarget(document.getElementById("unified-console-opener-slot"))
    update()

    // si la barre latérale se replie / se déplie, on re-résout la cible
    const onSidebarEvt = () => update()
    window.addEventListener("sidebar-collapsed-changed", onSidebarEvt)

    // optionnel: suivre l’apparition tardive du slot
    const mo = new MutationObserver(update)
    mo.observe(document.body, { childList: true, subtree: true })

    return () => {
      window.removeEventListener("sidebar-collapsed-changed", onSidebarEvt)
      mo.disconnect()
    }
  }, [])

  // Pings : Apollo HEALTH/WS, API REST, Front, (special si local)
  const urlsHTTP = useMemo(() => {
    const base = [apolloHealth, config.API_URL, config.FRONT_STUDIO_URL, specialBase].filter(
      Boolean
    ) as string[]
    return Array.from(new Set(base))
  }, [apolloHealth, specialBase])
  const urlsWS = useMemo(() => [apolloWs].filter(Boolean) as string[], [apolloWs])

  // HEALTH targets : apollo ↦ readiness, api ↦ liveness, front ↦ meta.json, special ↦ liveness
  const healthTargets = useMemo(() => {
    const items: Array<{ base: string; kind: HealthKind; label: string }> = []
    if (config.APOLLO_SERVER_URL)
      items.push({ base: config.APOLLO_SERVER_URL, kind: "readiness", label: "apollo" })
    if (config.API_URL) items.push({ base: config.API_URL, kind: "liveness", label: "api" })
    if (config.FRONT_STUDIO_URL)
      items.push({ base: config.FRONT_STUDIO_URL, kind: "meta", label: "front" })
    if (specialBase) items.push({ base: specialBase, kind: "liveness", label: "special-5" })
    return items
  }, [specialBase])

  // Hotkey
  useEffect(() => {
    let last = 0
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement
      const inEditable =
        t?.tagName === "INPUT" ||
        t?.tagName === "TEXTAREA" ||
        t?.getAttribute?.("contenteditable") === "true"
      if (inEditable) return
      if (hotkeyMatch(e)) {
        const n = performance.now()
        if (n - last < 250) return
        last = n
        e.preventDefault()
        setOpen(v => !v)
      }
    }
    window.addEventListener("keydown", onKey, { passive: true } as any)
    return () => window.removeEventListener("keydown", onKey)
  }, [])

  if (crash) throw new Error("debug: intentional render crash")
  const triggerRenderCrash = () => {
    if (confirm("Déclencher un crash volontaire (test ErrorBoundary) ?")) setCrash(true)
  }
  const triggerAsyncError = () => {
    if (confirm("Déclencher une erreur asynchrone (test Sentry global) ?"))
      setTimeout(() => {
        throw new Error("debug: uncaught async error")
      }, 0)
  }

  const checkMeta = useCallback(async () => {
    try {
      const r = await fetch(`/meta.json?ts=${Date.now()}`, { cache: "no-store" })
      const j = await r.json()
      setMetaRel(j.release)
    } catch {}
  }, [])

  const doPingOnce = useCallback(async () => {
    if (!urlsHTTP.length && !urlsWS.length) return
    setBusy(true)
    try {
      for (const u of urlsHTTP) {
        const r = await pingHTTP(u)
        setSamples(prev => ({ ...prev, [u]: [...(prev[u] ?? []), r].slice(-100) }))
      }
      for (const u of urlsWS) {
        const r = await pingWS(u)
        setSamples(prev => ({ ...prev, [u]: [...(prev[u] ?? []), r].slice(-50) }))
      }
    } finally {
      setBusy(false)
    }
  }, [urlsHTTP, urlsWS])

  const doHealthOnce = useCallback(async () => {
    if (!healthTargets.length) return
    setBusy(true)
    try {
      for (const t of healthTargets) {
        const h = await fetchHealth(t.base, t.kind, t.label)
        const key = `${t.label}:${t.base}:${t.kind}`
        setHealthData(prev => ({ ...prev, [key]: [...(prev[key] ?? []), h].slice(-100) }))
        // stocke latence en sparkline
        const asPing: PingHTTP = {
          kind: "http",
          url: h.url,
          ok: h.ok,
          status: h.ok ? 200 : undefined,
          totalMs: h.totalMs,
          at: h.at,
        }
        setSamples(prev => ({
          ...prev,
          [h.url]: [...((prev[h.url] as PingHTTP[]) ?? []), asPing].slice(-100),
        }))
      }
    } finally {
      setBusy(false)
    }
  }, [healthTargets])

  useEffect(() => {
    if (!burst) return
    let n = 0
    const id = setInterval(async () => {
      n++
      await doPingOnce()
      if (n >= 10) setBurst(false)
    }, 2000)
    return () => clearInterval(id)
  }, [burst, doPingOnce])

  useEffect(() => {
    if (!healthBurst) return
    let n = 0
    const id = setInterval(async () => {
      n++
      await doHealthOnce()
      if (n >= 10) setHealthBurst(false)
    }, 2000)
    return () => clearInterval(id)
  }, [healthBurst, doHealthOnce])

  // PERF
  const perfEnabled = tab === "perf"
  const { fps, history: fpsH } = useFPS(perfEnabled)
  const { lag, p95: lagP95, history: lagH } = useLoopLag(perfEnabled, 500)
  const heap = useJsHeap(perfEnabled)
  const lt = useLongTasks(perfEnabled)
  const envInfo = useEnvInfo()

  // copier dans le presse-papiers
  const copy = useCallback(async (v?: string | number, label?: string) => {
    const txt = v == null ? "" : String(v)
    if (!txt) return alert(`${label ?? "Valeur"} vide`)
    try {
      await navigator.clipboard.writeText(txt)
      alert(`${label ?? "Valeur"} copiée`)
    } catch {
      const ta = document.createElement("textarea")
      ta.value = txt
      document.body.appendChild(ta)
      ta.select()
      try {
        document.execCommand("copy")
        alert(`${label ?? "Valeur"} copiée`)
      } finally {
        document.body.removeChild(ta)
      }
    }
  }, [])

  // Opener (pas en prod)
  const showEnvOpener = config.ENVIRONMENT !== "production"
  if (!open) {
    if (!showEnvOpener) return null

    const inline = !!openerTarget
    const btn = (
      <button
        onClick={() => setOpen(true)}
        style={
          inline
            ? {
                // rendu intégré dans le footer/aside
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 18,
                border: "1px solid #334155",
                background: "#0b1220",
                color: "#e5e7eb",
                opacity: 0.9,
                fontSize: 12,
                lineHeight: 1,
              }
            : {}
        }
        title="UnifiedConsole (Ctrl+Shift+U)"
      >
        {(config.ENVIRONMENT ?? "DEV").toUpperCase()}
      </button>
    )

    if (!openerTarget) return null // hide when sidebar is collapsed (no slot)
    return createPortal(btn, openerTarget)
  }
  // const showEnvOpener = config.ENVIRONMENT !== "production"
  // if (!open) {
  //   return showEnvOpener ? (
  //     <button
  //       onClick={() => setOpen(true)}
  //       style={{
  //         position: "fixed",
  //         left: "max(8px, env(safe-area-inset-right))",
  //         bottom: "max(8px, env(safe-area-inset-bottom))",
  //         zIndex: 99998,
  //         padding: "6px 10px",
  //         borderRadius: 18,
  //         border: "1px solid #334155",
  //         background: "#0b1220",
  //         color: "#e5e7eb",
  //         opacity: 0.6,
  //       }}
  //       title="UnifiedConsole (Ctrl+Shift+U)"
  //     >
  //       {(config.ENVIRONMENT ?? "DEV").toUpperCase()}
  //     </button>
  //   ) : null
  // }

  // UI helpers
  const Btn = (p: any) => (
    <button
      {...p}
      style={{
        padding: "6px 10px",
        borderRadius: 6,
        border: "1px solid #334155",
        background: "#0b1220",
        color: "#e5e7eb",
        cursor: "pointer",
      }}
    />
  )
  const Section: React.FC<{ title: string; right?: React.ReactNode }> = ({
    title,
    right,
    children,
  }) => (
    <section style={{ border: "1px solid #1f2937", borderRadius: 8, padding: 12 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
        <div style={{ fontWeight: 700, flex: 1 }}>{title}</div>
        {right}
      </div>
      {children}
    </section>
  )
  const deriveStats = (arr: PingAny[]) => {
    const http = arr.filter((x): x is PingHTTP => x.kind === "http")
    const totals = http.map(x => x.totalMs)
    return {
      last: totals.at(-1),
      min: totals.length ? Math.min(...totals) : undefined,
      max: totals.length ? Math.max(...totals) : undefined,
      avg: mean(totals),
      p95: p95(totals),
    }
  }
  const Badge = ({ s }: { s?: string }) => {
    const ok = s === "ok" || s === "up"
    const c = ok ? "#10b981" : "#ef4444"
    return (
      <span
        style={{
          border: `1px solid ${c}`,
          color: c,
          padding: "2px 6px",
          borderRadius: 999,
          fontSize: 12,
        }}
      >
        {s ?? "unknown"}
      </span>
    )
  }
  const PerfBadge: React.FC<{ p95?: number }> = ({ p95 }) => {
    let c = "#6b7280",
      txt = "—"
    if (typeof p95 === "number") {
      if (p95 < P95_WARN) {
        c = "#10b981"
        txt = `p95 ${p95}ms`
      } else if (p95 < P95_CRIT) {
        c = "#f59e0b"
        txt = `p95 ${p95}ms`
      } else {
        c = "#ef4444"
        txt = `p95 ${p95}ms`
      }
    }
    return (
      <span
        style={{
          border: `1px solid ${c}`,
          color: c,
          padding: "2px 6px",
          borderRadius: 999,
          fontSize: 12,
          marginLeft: 6,
        }}
      >
        {txt}
      </span>
    )
  }

  return (
    <div
      style={{
        position: "fixed",
        top: "6%",
        left: "5%",
        right: "5%",
        bottom: "6%",
        background: "#0b1220",
        color: "#e5e7eb",
        border: "1px solid #1f2937",
        borderRadius: 12,
        padding: 12,
        zIndex: 99999,
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 12px 40px rgba(0,0,0,0.45)",
        fontFamily:
          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono','Courier New', monospace",
      }}
    >
      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        {(["debug", "health", "perf", "sync", "recovery"] as Tab[]).map(t => (
          <Btn key={t} onClick={() => setTab(t)} style={{ fontWeight: tab === t ? 700 : 400 }}>
            {t.toUpperCase()}
          </Btn>
        ))}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <Btn onClick={() => setOpen(false)}>Fermer (Ctrl+Shift+U)</Btn>
        </div>
      </div>
      <div style={{ overflow: "auto", flex: 1, borderTop: "1px solid #1f2937", paddingTop: 8 }}>
        {tab === "debug" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Section title="Build" right={<Btn onClick={checkMeta}>Lire meta.json</Btn>}>
              <div>env: {envLabel}</div>
              <div>release: {config.RELEASE}</div>
              <div>buildDate: {config.BUILD_DATE}</div>
              <div>release(meta): {metaRel ?? "?"}</div>
              <div>ws status: {wsStatus || "n/a"}</div>
              <div>apollo health: {apolloHealth}</div>
              <div>apollo ws: {apolloWs}</div>
              <div>api: {config.API_URL || "—"}</div>
              <div>front: {config.FRONT_STUDIO_URL || "—"}</div>
              {specialBase && <div>special-5: {specialBase}</div>}
              <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Btn
                  onClick={() => {
                    Sentry.captureMessage("debug: manual message", s => {
                      s.setLevel("info")
                      s.setTag("debug.overlay", "true")
                      s.setContext("env", { env: envLabel })
                      return s
                    })
                    alert("Message Sentry envoyé.")
                  }}
                >
                  Sentry: Message
                </Btn>
                <Btn
                  onClick={() => {
                    try {
                      throw new Error("debug: manual exception")
                    } catch (e) {
                      Sentry.captureException(e, s => {
                        s.setTag("debug.overlay", "true")
                        s.setContext("env", { env: envLabel })
                        return s
                      })
                    }
                    alert("Exception Sentry envoyée.")
                  }}
                >
                  Sentry: Exception
                </Btn>
                <Btn
                  onClick={async () => {
                    await Sentry.startSpan(
                      { name: "debug-span", op: "debug", attributes: { from: "overlay" } },
                      async () => {
                        await new Promise(r => setTimeout(r, 500))
                      }
                    )
                    alert("Span 500ms envoyée.")
                  }}
                >
                  Sentry: Span
                </Btn>
                <Btn
                  onClick={triggerRenderCrash}
                  style={{ color: "#ef4444", borderColor: "#ef4444" }}
                >
                  CRASH volontaire
                </Btn>
                <Btn
                  onClick={triggerAsyncError}
                  style={{ color: "#f59e0b", borderColor: "#f59e0b" }}
                >
                  Throw async
                </Btn>
              </div>
            </Section>

            <Section
              title="Endpoints"
              right={
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <Btn disabled={busy || (!urlsHTTP.length && !urlsWS.length)} onClick={doPingOnce}>
                    {busy ? "Ping..." : "Ping"}
                  </Btn>
                  <Btn
                    disabled={!urlsHTTP.length && !urlsWS.length}
                    onClick={() => {
                      setBurst(true)
                      void doPingOnce()
                    }}
                  >
                    Burst 20s
                  </Btn>
                  <Btn
                    onClick={async () => {
                      const r = await probeGraphQLRoundtrip(
                        `${config.APOLLO_SERVER_URL}/graphql`,
                        props.accessToken
                      )
                      alert(
                        `GraphQL RT: ${r.ok ? "ok" : "err"} • ${r.ms}ms${
                          r.status ? ` (${r.status})` : ""
                        }`
                      )
                    }}
                  >
                    GQL Round-trip
                  </Btn>
                  <Btn
                    onClick={async () => {
                      const r = await probeGraphQLWsAck(
                        `${config.APOLLO_WS_URL}`,
                        props.accessToken
                      )
                      alert(
                        `WS Ack: ${r.ok ? "ok" : "err"}${r.ms ? ` • ${r.ms}ms` : ""}${
                          r.note ? ` • ${r.note}` : ""
                        }`
                      )
                    }}
                  >
                    WS Ack
                  </Btn>
                </div>
              }
            >
              {!urlsHTTP.length && !urlsWS.length && (
                <div style={{ opacity: 0.8 }}>Aucune URL à pinger.</div>
              )}
              {[...urlsHTTP, ...urlsWS].map(u => {
                const arr = samples[u] ?? []
                const stats = deriveStats(arr)
                const last = arr.at(-1)
                const spark = (arr.filter(a => (a as any).totalMs) as PingHTTP[]).map(a => ({
                  at: a.at,
                  totalMs: (a as any).totalMs,
                }))
                return (
                  <div
                    key={u}
                    style={{ borderTop: "1px dashed #334155", paddingTop: 8, marginTop: 8 }}
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        wordBreak: "break-all",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <span>{u}</span>
                      <PerfBadge p95={stats.p95} />
                    </div>
                    {last?.kind === "http" && (
                      <div style={{ fontSize: 12, opacity: 0.95 }}>
                        last: {last.ok ? "ok" : "err"}
                        {last.status ? ` (${last.status})` : ""} • total {last.totalMs}ms
                        {typeof (last as any).ttfbMs === "number"
                          ? ` • ttfb ${(last as any).ttfbMs}ms`
                          : ""}
                        {typeof (last as any).dlMs === "number"
                          ? ` • dl ${(last as any).dlMs}ms`
                          : ""}
                        {(last as any).protocol ? ` • proto ${(last as any).protocol}` : ""}
                        {typeof (last as any).transferSize === "number"
                          ? ` • xfer ${(last as any).transferSize}B`
                          : ""}
                        {typeof (last as any).bodySize === "number"
                          ? ` • body ${(last as any).bodySize}B`
                          : ""}
                        {(last as any).fromCache ? " • cache" : ""}
                        {last.note ? ` • ${last.note}` : ""}
                      </div>
                    )}
                    {last?.kind === "ws" && (
                      <div style={{ fontSize: 12, opacity: 0.95 }}>
                        last: {last.ok ? "ok" : "err"}
                        {typeof last.connectMs === "number" ? ` • connect ${last.connectMs}ms` : ""}
                        {typeof last.closeCode === "number" ? ` • close ${last.closeCode}` : ""}
                        {last.closeReason ? ` (${last.closeReason})` : ""}
                        {last.note ? ` • ${last.note}` : ""}
                      </div>
                    )}
                    {!!arr.length && (
                      <>
                        <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>
                          stats: {stats.last ?? "—"}ms last • {stats.min ?? "—"} /{" "}
                          {stats.avg ?? "—"} / p95 {stats.p95 ?? "—"} / {stats.max ?? "—"} ms
                        </div>
                        {!!spark.length && (
                          <div style={{ marginTop: 6 }}>
                            <LatencyChart data={spark} />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )
              })}
              <div style={{ fontSize: 11, opacity: 0.7, marginTop: 8 }}>
                * Pour TTFB/protocol/transferSize: <code>Timing-Allow-Origin: *</code> côté backend.
              </div>
            </Section>
          </div>
        )}

        {tab === "health" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Section
              title="Health (apollo: readiness • api: liveness • front: meta.json • special: liveness)"
              right={
                <div style={{ display: "flex", gap: 8 }}>
                  <Btn disabled={busy || !healthTargets.length} onClick={doHealthOnce}>
                    {busy ? "Poll..." : "Poll"}
                  </Btn>
                  <Btn
                    disabled={!healthTargets.length}
                    onClick={() => {
                      setHealthBurst(true)
                      void doHealthOnce()
                    }}
                  >
                    Burst 20s
                  </Btn>
                </div>
              }
            >
              {!healthTargets.length && (
                <div style={{ opacity: 0.8 }}>Aucune cible santé configurée.</div>
              )}
              {healthTargets.map(t => {
                const key = `${t.label}:${t.base}:${t.kind}`
                const arr = healthData[key] ?? []
                const last = arr.at(-1)
                const spark = arr.map(a => ({ at: a.at, totalMs: a.totalMs }))
                const url =
                  t.base.replace(/\/$/, "") +
                  (t.kind === "meta" ? "/meta.json" : `/health/${t.kind}`)
                return (
                  <div
                    key={key}
                    style={{ borderTop: "1px dashed #334155", paddingTop: 8, marginTop: 8 }}
                  >
                    <div style={{ fontWeight: 600, wordBreak: "break-all" }}>
                      {t.label} → {url} &nbsp; <Badge s={last?.status} />
                    </div>
                    {last && (
                      <div style={{ fontSize: 12, opacity: 0.95, marginTop: 2 }}>
                        total {last.totalMs}ms{last.note ? ` • ${last.note}` : ""}
                      </div>
                    )}
                    {last?.indicators && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                        {Object.entries(last.indicators).map(([k, v]) => (
                          <span
                            key={k}
                            style={{
                              border: `1px solid ${
                                v === "up" || v === "ok" ? "#10b981" : "#ef4444"
                              }`,
                              color: v === "up" || v === "ok" ? "#10b981" : "#ef4444",
                              padding: "2px 6px",
                              borderRadius: 999,
                              fontSize: 12,
                            }}
                          >
                            {k}: {v}
                          </span>
                        ))}
                      </div>
                    )}
                    {!!spark.length && (
                      <div style={{ marginTop: 6 }}>
                        <LatencyChart data={spark} />
                      </div>
                    )}
                  </div>
                )
              })}
              <div style={{ fontSize: 11, opacity: 0.7, marginTop: 8 }}>
                * Front health via <code>/meta.json</code> (affiche <i>release</i> si présente). *
                Si une route n’existe pas, statut HTTP s’affiche (ex: http:404), c’est OK.
              </div>
            </Section>
          </div>
        )}

        {tab === "perf" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Section title="Performance navigateur">
              <div>FPS: {fps}</div>
              <div>
                Event-loop lag: last {lag}ms • p95 {lagP95}ms
              </div>
              <div>
                JS Heap: {heap.used ?? "?"}/{heap.total ?? "?"} MB
              </div>
              <div>
                Long tasks: {lt.count} • max {lt.maxMs}ms • total {lt.totalMs}ms
              </div>
              {!!lagH.length && (
                <div style={{ marginTop: 6 }}>
                  <LatencyChart data={lagH.map(x => ({ at: x.at, totalMs: x.ms }))} />
                </div>
              )}
              {!!fpsH.length && (
                <div style={{ marginTop: 6 }}>
                  <LatencyChart data={fpsH.map(x => ({ at: x.at, totalMs: x.v }))} />
                </div>
              )}
            </Section>
            <Section title="Environnement">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                <div>CPU: {envInfo.cores} cores</div>
                <div>RAM (approx): {envInfo.deviceMemory ?? "?"} GB</div>
                <div>TZ: {envInfo.tz}</div>
                <div>Langs: {envInfo.langs}</div>
                <div>
                  Net: {envInfo.net.type || "?"} • {envInfo.net.downlink ?? "?"}Mbps • rtt{" "}
                  {envInfo.net.rtt ?? "?"}ms {envInfo.net.saveData ? "• saveData" : ""}
                </div>
                <div>
                  Storage: {envInfo.storage.usage ?? "?"}/{envInfo.storage.quota ?? "?"} MB
                </div>
                <div>
                  Batterie: {envInfo.battery.level ?? "?"}% {envInfo.battery.charging ? "⚡" : ""}
                </div>
                <div style={{ gridColumn: "1 / -1", opacity: 0.8, fontSize: 12 }}>
                  UA: {envInfo.ua}
                </div>
              </div>
            </Section>
          </div>
        )}

        {tab === "sync" && (
          <Section title="Synchronisation">
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <Btn onClick={() => props.onToggleSync(!props.isSyncing)}>
                {props.isSyncing ? "Arrêter" : "Démarrer"} la synchro (30s)
              </Btn>
              {props.onSyncOnce && <Btn onClick={props.onSyncOnce}>Envoyer 1 fois</Btn>}
              <Btn onClick={() => copy(props.businessId, "Business ID")}>Copier Business ID</Btn>
              <Btn onClick={() => copy(props.user.id, "User ID")}>Copier User ID</Btn>
              <Btn onClick={() => copy(props.accessToken, "Access Token")}>Copier Access Token</Btn>
              <label style={{ marginLeft: 8, fontSize: 12, opacity: 0.9 }}>
                <input
                  type="checkbox"
                  checked={showSecrets}
                  onChange={e => setShowSecrets(e.target.checked)}
                  style={{ marginRight: 6 }}
                />
                Afficher les valeurs
              </label>
            </div>
            <div style={{ marginTop: 8, opacity: 0.9 }}>
              <div>
                Business ID:{" "}
                <code>{mask(props.businessId ? String(props.businessId) : "", showSecrets)}</code>
              </div>
              <div>
                Access Token: <code>{mask(props.accessToken, showSecrets)}</code>
              </div>
              <div style={{ marginTop: 8, opacity: 0.85 }}>
                Etat : {props.isSyncing ? "en cours…" : "arrêtée"}
              </div>
              {specialBase && (
                <div style={{ marginTop: 8, opacity: 0.8, fontSize: 12 }}>
                  Service spécial (5): {specialBase} — peut être OFF, c’est OK.
                </div>
              )}
            </div>
          </Section>
        )}

        {tab === "recovery" && (
          <Section title="Récupération">
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Btn
                onClick={async () => {
                  const ok = confirm("Purger le storage applicatif puis redémarrer ?")
                  if (!ok) return
                  try {
                    await purgeAppStorage()
                  } catch {}
                  setTimeout(() => {
                    window.location.assign("/")
                  }, 50)
                }}
              >
                Purger cache & redémarrer
              </Btn>
            </div>
          </Section>
        )}
      </div>
    </div>
  )
}
